import { create } from 'zustand';

// THE LIVING CITY, client half (Khari, 2026-10-22/23: the artists live in the
// World — "a walkable show… the user is the spectator in THEIR world").
//
// The server's stage manager (API services/city) owns the city: every resident
// artist's day as a timeline, the conversations under way, who has stopped to
// talk to a visitor. It sends that whole (`city_state`) and the World works out
// where every body is from it and the shared clock (city/cityMotion). Only
// changes travel. This store is where they land.
//
// `live` turns true on the first snapshot. Until then — an API that predates
// the city, or a socket that never connects — the street keeps its old static
// residents (NpcManager), so the world is never empty because of this.
export const useCityStore = create((set, get) => ({
  live: false,
  snap: null,             // the last city_state
  byId: {},               // avatarId → resident (segs)
  convs: {},              // id → conversation (with lines)
  holds: {},              // avatarId → { since, until, by }
  stories: {},            // id → City Story { a, title, scenes, lines, score, … }
  // Sets on the plaza stage, song by song, and the DJ's heads-up for each.
  shows: [],              // [{ a, name, place, t0, t1, tracks: [{ title, url, art, durMs, at }], announce, inside?, kind? }]
  stageNow: null,         // what this visitor hears from the stage { a, name, title, art, where? } (CityStage; VenueStage indoors)
  // A set INSIDE the venue you are in (components/VenueStage, 2026-10-30): what
  // its wall screens show, and the words being said into the mic, captioned.
  venueNow: null,         // { room, a, name, live, title, art, arts, words, kind }
  venueCaption: null,     // { name, text }
  soundNow: null,         // the street music clearly heard here { id, title, artist, art, where } (components/city/CitySounds)
  djNow: null,            // the DJ's heads-up being said { host, text, until }
  // Voiced conversations are for paying members; anyone else gets one a visit.
  allowance: null,        // { paying, free: conv id that was theirs | null } (server)
  capped: null,           // the conversation you are listening to that will not go on for you
  // Visitor replies the artist said out loud (bystanders see them).
  said: {},               // avatarId → { text, at }
  // Who is speaking a conversation line right now (for the body's head and face).
  speaking: {},           // avatarId → until (ms, server clock)
  // The artist THIS visitor stopped to talk to.
  engaged: null,          // { avatarId, mustGoAt, nextActivity }

  // A REPLAY (city/replay) owns the city while it plays: the live snapshots
  // keep arriving and are set aside until it is over.
  replay: false,
  setSnap: (snap, opts = {}) => {
    if (!snap || !Array.isArray(snap.residents)) return;
    if (opts.replay) set({ replay: true });
    else if (get().replay) return;
    const byId = {};
    for (const r of snap.residents) byId[r.avatarId] = r;
    const convs = {};
    const prev = get().convs;
    for (const c of snap.convs || []) {
      // Keep lines that arrived by `city_line` but are not in the snapshot yet.
      const had = prev[c.id]?.lines || [];
      const lines = [...(c.lines || [])];
      for (const l of had) if (!lines.some((x) => x.idx === l.idx)) lines.push(l);
      lines.sort((a, b) => a.idx - b.idx);
      convs[c.id] = { ...c, lines };
    }
    const holds = {};
    for (const h of snap.holds || []) holds[h.avatarId] = h;
    // City Stories: each artist's episode today (scenes, lines, its song).
    const stories = {};
    for (const st of snap.stories || []) stories[st.id] = st;
    set({ live: true, snap, byId, convs, holds, stories, shows: Array.isArray(snap.shows) ? snap.shows : [] });
  },

  addLine: (line) => {
    if (!line?.conv || get().replay) return;
    const convs = { ...get().convs };
    const c = convs[line.conv];
    if (!c) return;   // arrives with the next snapshot
    const lines = c.lines.filter((l) => l.idx !== line.idx);
    lines.push(line);
    lines.sort((a, b) => a.idx - b.idx);
    convs[line.conv] = { ...c, lines };
    set({ convs });
  },

  addSaid: (m) => {
    if (!m?.avatarId || !m.text) return;
    set({ said: { ...get().said, [m.avatarId]: { text: m.text, at: m.ts || Date.now() } } });
  },

  setSpeaking: (avatarId, until) => {
    const cur = get().speaking;
    if (cur[avatarId] === until) return;
    set({ speaking: { ...cur, [avatarId]: until } });
  },

  setEngaged: (engaged) => set({ engaged }),
}));

if (import.meta.env.DEV && typeof window !== 'undefined') window.__cityStore = useCityStore;
